import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { fabric } from 'fabric';
import EllipseIcon from '../images/Icon20_Oval.svg';
import {
  setCursorTooltip,
  updateTool,
} from '../../../redux/actions/fabric_overlay';
import Button from './Toolbar/Button';
import { connect } from 'react-redux';
import { getThicknessPx, hexToRgba } from '../helpers/fabricHelpers';
import { __ } from '../../../core/utils';

const Ellipse = ({
  fabric_overlay: {
    activeTool,
    viewer,
    fabricOverlay,
    color,
    selectedThickness,
  },
  isActive,
  plan,
  user_id,
  user_full_name,
}) => {
  const [state, setState] = useState({
    ellipse: null,
    isMouseDown: false,
    origX: null,
    origY: null,
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (!isActive || !fabricOverlay) return;

    fabricOverlay.fabricCanvas().defaultCursor = 'crosshair';
    fabricOverlay.fabricCanvas().selection = false;

    return () => {
      fabricOverlay.fabricCanvas().discardActiveObject();
      //fabricOverlay.fabricCanvas().requestRenderAll();
    };
  }, [isActive, fabricOverlay]);

  useEffect(() => {
    if (!isActive || !fabricOverlay) return;

    const canvas = fabricOverlay.fabricCanvas();

    canvas.defaultCursor = 'crosshair';
    canvas.hoverCursor = 'crosshair';

    setCursorTooltip(__('canvas.tooltip.shape.start'));

    canvas.getObjects().forEach((obj) => {
      obj.selectable = false;
      obj.evented = false;
    });

    function onMouseDown(options) {
      if (options.target || !isActive) return;

      const rawPointer = canvas.getPointer(options.e);
      const pointer = {
        x: Math.max(0, Math.min(plan.width, rawPointer.x)),
        y: Math.max(0, Math.min(plan.height, rawPointer.y)),
      };

      const origX = pointer.x;
      const origY = pointer.y;

      const ellipse = new fabric.Ellipse({
        left: origX,
        top: origY,
        originX: 'left',
        originY: 'top',
        width: 1,
        height: 1,
        fill: hexToRgba(color.hex, 0.05),
        stroke: color.hex,
        strokeWidth: getThicknessPx(selectedThickness.key),
        objectType: 'ellipse',
        selectable: true,
        hasControls: false,
        hasBorders: false,
        userID: user_id,
        userFullName: user_full_name,
      });

      canvas.add(ellipse);

      setState({
        ellipse,
        isMouseDown: true,
        origX,
        origY,
      });
    }

    function onMouseMove(options) {
      if (
        !isActive ||
        !stateRef.current.isMouseDown ||
        !stateRef.current.ellipse
      )
        return;

      const rawPointer = canvas.getPointer(options.e);
      const pointer = {
        x: Math.max(0, Math.min(plan.width, rawPointer.x)),
        y: Math.max(0, Math.min(plan.height, rawPointer.y)),
      };

      let rx = Math.abs(stateRef.current.origX - pointer.x) / 2;
      let ry = Math.abs(stateRef.current.origY - pointer.y) / 2;

      if (rx > stateRef.current.ellipse.strokeWidth) {
        rx -= stateRef.current.ellipse.strokeWidth / 2;
      }
      if (ry > stateRef.current.ellipse.strokeWidth) {
        ry -= stateRef.current.ellipse.strokeWidth / 2;
      }

      const ellipse = stateRef.current.ellipse;

      ellipse.set({
        rx,
        ry,
        originX: pointer.x < stateRef.current.origX ? 'right' : 'left',
        originY: pointer.y < stateRef.current.origY ? 'bottom' : 'top',
      });

      canvas.requestRenderAll();

      setCursorTooltip(__('canvas.tooltip.shape.drawing'));
    }

    function onMouseUp() {
      if (!isActive || !stateRef.current.ellipse) return;

      viewer.setMouseNavEnabled(true);

      setState({
        ellipse: null,
        isMouseDown: false,
        origX: null,
        origY: null,
      });

      updateTool({ tool: 'hand' });

      // /setCursorTooltip(__('canvas.tooltip.shape.start'));
    }

    const onPointerDown = (e) => {
      viewer.setMouseNavEnabled(false);
    };

    canvas.upperCanvasEl.addEventListener('pointerdown', onPointerDown);

    canvas.on('mouse:down', onMouseDown);
    canvas.on('mouse:move', onMouseMove);
    canvas.on('mouse:up', onMouseUp);

    return () => {
      canvas.off('mouse:down', onMouseDown);
      canvas.off('mouse:move', onMouseMove);
      canvas.off('mouse:up', onMouseUp);
      canvas.upperCanvasEl.removeEventListener('pointerdown', onPointerDown);
    };
  }, [fabricOverlay, isActive, color, selectedThickness]);

  const toggleTool = () => {
    updateTool({ tool: 'ellipse' });
  };

  return (
    <Button
      id='ellipse'
      label={__('canvas.toolbar.ellipse')}
      icon={EllipseIcon}
      active={activeTool === 'ellipse'}
      onClick={toggleTool}
    />
  );
};

Ellipse.propTypes = {
  isActive: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => {
  return {
    fabric_overlay: state.fabric_overlay,
    user_id: state.auth.id,
    user_full_name: state.auth.firstname + ' ' + state.auth.lastname,
  };
};

export default connect(mapStateToProps, null)(Ellipse);
